import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Phone, Mail, Building2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ExportButtons from "../../components/ExportButtons";
import { maskPhoneBR, sanitizePhone } from "../../lib/phone";

const STAGES = [
  { id: "novo", label: "Novo", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "contato", label: "Contato", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { id: "qualificado", label: "Qualificado", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "proposta", label: "Proposta", color: "bg-orange-50 text-orange-700 border-orange-200" },
  { id: "ganho", label: "Ganho", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "perdido", label: "Perdido", color: "bg-gray-50 text-gray-600 border-gray-200" },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", interest: "", source: "site" });

  const load = () => api.get("/leads").then(({ data }) => setLeads(data));
  useEffect(() => { load(); }, []);

  const move = async (id, stage) => {
    await api.put(`/leads/${id}`, { stage });
    load();
  };

  const remove = async (id) => {
    if (!confirm("Remover este lead?")) return;
    await api.delete(`/leads/${id}`);
    toast.success("Lead removido");
    load();
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, phone: sanitizePhone(form.phone) });
      toast.success("Lead criado");
      setShowForm(false);
      setForm({ name: "", email: "", phone: "", company: "", interest: "", source: "site" });
      load();
    } catch {
      toast.error("Erro ao criar lead");
    }
  };

  return (
    <div data-testid="admin-leads">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">CRM Pipeline</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Leads & Oportunidades</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <ExportButtons resource="leads" />
          <button onClick={() => setShowForm(!showForm)} className="flex-1 sm:flex-none bg-[#14213D] text-white px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#1F335E]" data-testid="new-lead-btn">
            <Plus size={16} /> Novo Lead
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="lead-form">
          <input required placeholder="Nome *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input placeholder="E-mail" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input placeholder="Telefone" value={form.phone} onChange={e => setForm({ ...form, phone: maskPhoneBR(e.target.value) })} type="tel" inputMode="tel" autoComplete="tel" className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input placeholder="Empresa" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input placeholder="Interesse" value={form.interest} onChange={e => setForm({ ...form, interest: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm">
            <option value="site">Site</option>
            <option value="instagram">Instagram</option>
            <option value="google">Google</option>
            <option value="indicacao">Indicação</option>
            <option value="outro">Outro</option>
          </select>
          <button type="submit" className="md:col-span-3 bg-[#D4AF37] text-[#14213D] font-semibold py-2.5 rounded text-sm" data-testid="save-lead">Salvar Lead</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="kanban-board">
        {STAGES.map((stage) => {
          const stageLeads = leads.filter(l => l.stage === stage.id);
          return (
            <div key={stage.id} className="bg-gray-50/80 border border-gray-200 rounded-lg p-3 min-h-[300px]" data-testid={`stage-${stage.id}`}>
              <div className={`text-xs uppercase tracking-wider font-bold px-2 py-1 rounded mb-3 border ${stage.color}`}>
                {stage.label} <span className="ml-1 opacity-60">({stageLeads.length})</span>
              </div>
              <div className="space-y-2">
                {stageLeads.map((l) => (
                  <div key={l.id} className="bg-white border border-gray-200 rounded p-3 hover:shadow-sm transition" data-testid={`lead-${l.id}`}>
                    <div className="font-semibold text-sm text-[#14213D]">{l.name}</div>
                    {l.company && <div className="text-xs text-gray-500 flex items-center gap-1 mt-1"><Building2 size={11} />{l.company}</div>}
                    {l.email && <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Mail size={11} />{l.email}</div>}
                    {l.phone && <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone size={11} />{l.phone}</div>}
                    {l.interest && <div className="text-[11px] mt-2 text-[#D4AF37] font-semibold">{l.interest}</div>}
                    <div className="mt-2 flex items-center gap-1">
                      <select
                        value={l.stage}
                        onChange={(e) => move(l.id, e.target.value)}
                        className="text-[10px] border border-gray-200 rounded px-1.5 py-1 flex-1"
                      >
                        {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                      <button onClick={() => remove(l.id)} className="text-gray-400 hover:text-red-600" data-testid={`del-lead-${l.id}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
